import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Plus, Trash2, ExternalLink, Users } from "lucide-react";

const PLATFORM = {
  facebook: { label: "Facebook", cls: "bg-blue-100 text-blue-800" },
  instagram: { label: "Instagram", cls: "bg-pink-100 text-pink-800" },
  beide: { label: "FB + IG", cls: "bg-violet-100 text-violet-800" },
};

export function SocialGroups({ accountKey, isAdmin }) {
  const [items, setItems] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", platform: "facebook", url: "", relevance: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/social-groups", { params: { account: accountKey } }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [accountKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!form.name.trim()) return toast.error("Bitte Gruppennamen angeben");
    setSaving(true);
    try {
      await api.post("/social-groups", { ...form, account_key: accountKey });
      toast.success(`Gruppe „${form.name}“ angelegt ✅`);
      setForm({ name: "", platform: "facebook", url: "", relevance: "" });
      setAddOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (g) => {
    try { await api.delete(`/social-groups/${g.id}`); toast.success("Gruppe entfernt"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const cls = "w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm bg-white";

  return (
    <div data-testid="brand-groups-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <p className="text-sm text-neutral-500 max-w-xl">Facebook- & Instagram-Gruppen, die beim Posten relevant sind — so weiß jeder Creator, wo ein Beitrag zusätzlich geteilt werden sollte.</p>
        <button data-testid="brand-groups-add-btn" onClick={() => setAddOpen((v) => !v)} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3.5 py-2 rounded-lg bg-neutral-900 text-white hover:bg-neutral-800 transition">
          <Plus className="h-4 w-4" /> Gruppe hinzufügen
        </button>
      </div>

      {addOpen && (
        <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-4 mb-4 grid sm:grid-cols-2 gap-3" data-testid="brand-groups-add-form">
          <input data-testid="bg-name" placeholder="Gruppenname *" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className={cls} />
          <select data-testid="bg-platform" value={form.platform} onChange={(e) => setForm((p) => ({ ...p, platform: e.target.value }))} className={cls}>
            <option value="facebook">Facebook</option>
            <option value="instagram">Instagram</option>
            <option value="beide">Facebook + Instagram</option>
          </select>
          <input data-testid="bg-url" placeholder="Link zur Gruppe (optional)" value={form.url} onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))} className={cls} />
          <input data-testid="bg-relevance" placeholder="Wann relevant? z. B. Events, Vereinsleben" value={form.relevance} onChange={(e) => setForm((p) => ({ ...p, relevance: e.target.value }))} className={cls} />
          <button data-testid="bg-save" onClick={add} disabled={saving} className="sm:col-span-2 h-10 rounded-lg bg-violet-600 text-white text-sm font-bold hover:bg-violet-700 transition disabled:opacity-50 flex items-center justify-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Speichern
          </button>
        </div>
      )}

      {items === null ? (
        <div className="py-12 flex justify-center text-neutral-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-sm text-neutral-400 flex flex-col items-center gap-2" data-testid="brand-groups-empty">
          <Users className="h-8 w-8 text-neutral-300" /> Noch keine Gruppen hinterlegt.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {items.map((g) => (
            <div key={g.id} data-testid={`brand-group-${g.id}`} className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-neutral-900 text-sm">{g.name}</h3>
                <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${PLATFORM[g.platform]?.cls || ""}`}>{PLATFORM[g.platform]?.label || g.platform}</span>
              </div>
              {g.relevance && <p className="text-xs text-neutral-500 mt-1.5">📌 {g.relevance}</p>}
              <div className="flex items-center gap-2 mt-3">
                {g.url && (
                  <a href={g.url} target="_blank" rel="noreferrer" data-testid={`brand-group-link-${g.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-violet-600 hover:underline">
                    <ExternalLink className="h-3.5 w-3.5" /> Gruppe öffnen
                  </a>
                )}
                <div className="flex-1" />
                {isAdmin && (
                  <button data-testid={`brand-group-delete-${g.id}`} onClick={() => remove(g)} className="h-8 w-8 rounded-lg text-neutral-400 hover:text-rose-500 hover:bg-rose-50 flex items-center justify-center">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
